import type { RequestBody, ResponseBody } from '@reqshare/protocol';
import { CodeBlock } from '../CodeBlock/CodeBlock';
import './BodyBlock.scss';

interface BodyBlockProps {
  title: string;
  body?: RequestBody | ResponseBody;
}

function getLanguageFromMime(mimeType: string): string {
  if (mimeType.includes('json')) return 'json';
  if (mimeType.includes('xml') || mimeType.includes('html')) return 'xml';
  if (mimeType.includes('javascript')) return 'javascript';
  if (mimeType.includes('css')) return 'css';
  if (mimeType.includes('text/plain')) return 'text';
  return mimeType.split('/').pop() || 'text';
}

function formatBody(text: string, mimeType: string): string {
  if (mimeType.includes('json')) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }
  return text;
}

export function BodyBlock({ title, body }: BodyBlockProps) {
  if (!body || !body.text) {
    return null;
  }

  const language = getLanguageFromMime(body.mimeType);
  const formattedContent = formatBody(body.text, body.mimeType);

  return (
    <section className="body-block">
      <div className="body-block__header">
        <h3 className="body-block__title">{title}</h3>
        <div className="body-block__meta">
          <span className="body-block__mime">{body.mimeType}</span>
          <span className="body-block__size">{formatSize(body.size)}</span>
        </div>
      </div>
      <CodeBlock content={formattedContent} language={language} />
    </section>
  );
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
